// @flow

import { denormalize } from 'normalizr';
import concat from 'lodash/concat';
import uniq from 'lodash/uniq';
import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import { createSelector } from 'reselect';
import type {
  actionTypeSliceType,
  genericNestedStateSliceType,
  genericStateSliceType,
  reduxAction,
  statusSliceType,
} from '../dataStoreTypes';

const initialState = {};

const genericNestedReducer = (actionTypeSlice: actionTypeSliceType) => (
  state: genericStateSliceType = initialState,
  action: reduxAction,
) => {
  switch (action.type) {
    case actionTypeSlice.MANY.IFNEEDED:
      return {
        ...state,
        [action.parentId]: {
          ...state[action.parentId],
          status: 0,
          errors: {},
          deduplicationCounter:
            (state?.[action.parentId]?.deduplicationCounter || 0) + 1,
          sessionId: state?.[action.parentId]?.sessionId || -1,
        },
      };
    case actionTypeSlice.MANY.PENDING:
      return {
        ...state,
        [action.parentId]: {
          ...state[action.parentId],
          status: 1000,
          errors: {},
          deduplicationCounter:
            state?.[action.parentId]?.deduplicationCounter || 1,
          sessionId: state?.[action.parentId]?.sessionId || -1,
        },
      };
    case actionTypeSlice.MANY.SUCCESS: {
      const individualStatuses = action.ids.reduce(
        (acc, id) => ({
          ...acc,
          [id]: {
            status: 200,
            errors: {},
            deduplicationCounter: 0,
          },
        }),
        {},
      );
      return {
        ...state,
        [action.parentId]: {
          ...state[action.parentId],
          ids: uniq(concat([], state[action.parentId].ids || [], action.ids)),
          status: 200,
          errors: {},
          deduplicationCounter: 0,
          sessionId: action.sessionId,
          ...individualStatuses,
        },
      };
    }
    case actionTypeSlice.MANY.ERROR:
      return {
        ...state,
        [action.parentId]: {
          ...state[action.parentId],
          status: action.status,
          errors: action.errors,
          deduplicationCounter: 0,
          sessionId: state?.[action.parentId]?.sessionId || -1,
        },
      };
    case actionTypeSlice.SINGLE.IFNEEDED:
      return {
        ...state,
        [action.parentId]: {
          ...state[action.parentId],
          [action.id]: {
            status: 0,
            errors: {},
            deduplicationCounter:
              (state?.[action.parentId]?.[action.id]?.deduplicationCounter ||
                0) + 1,
          },
        },
      };
    case actionTypeSlice.SINGLE.PENDING:
      return {
        ...state,
        [action.parentId]: {
          ...state[action.parentId],
          [action.id]: {
            status: 1000,
            errors: {},
            deduplicationCounter:
              state?.[action.parentId]?.[action.id]?.deduplicationCounter || 1,
          },
        },
      };
    case actionTypeSlice.SINGLE.SUCCESS:
      return {
        ...state,
        [Number(action.parentId)]: {
          ...state[action.parentId],
          ids: uniq(concat([], state[action.parentId]?.ids || [], [action.id])),
          [action.id]: {
            status: 200,
            errors: {},
            deduplicationCounter: 0,
          },
        },
      };
    case actionTypeSlice.SINGLE.ERROR:
      return {
        ...state,
        [action.parentId]: {
          ...state[action.parentId],
          [action.id]: {
            status: action.status,
            errors: action.errors,
            deduplicationCounter: 0,
          },
        },
      };
    default:
      return state;
  }
};

export default genericNestedReducer;

export const genericNestedSelectorSet = <T>(
  schema: any,
  sliceSelector: any => genericNestedStateSliceType,
) => ({
  singleById: (id: number) => (state: any): T =>
    denormalize(id, schema, state.entities),
  all: (parentId: number) => (state: any): Array<T> =>
    denormalize(
      sliceSelector(state)[parentId]
        ? sliceSelector(state)[parentId].ids || []
        : [],
      [schema],
      state.entities,
    ),
  ids: (parentId: number) => (state: any): Array<number> =>
    sliceSelector(state)[parentId]
      ? sliceSelector(state)[parentId].ids || []
      : [],
  metaMany: (parentId: number) => (state: any) =>
    sliceSelector(state)[parentId],
  metaSingleById: (parentId: number, id: number) => (state: any) => {
    const slice = sliceSelector(state);
    if (slice[parentId]) {
      return slice[parentId][id];
    }
    return { status: 0, errors: {} };
  },
});

type genericNestedSubscriptionHookBundleType<T> = {
  singleById: (id: number) => typeof undefined | T,
  manyByParentId: () => { [number]: Array<T> },
  all: (parentId: number) => Array<T>,
  ids: (parentId: number) => Array<number>,
  metaMany: (parentId: number) => typeof undefined | genericStateSliceType,
  metaSingleById: (
    parentId: number,
    id: number,
  ) => typeof undefined | statusSliceType,
};

const genericMemoizedNestedSelectorSet = (
  schema,
  sliceSelector,
  parentSliceSelector,
) => ({
  singleById: () =>
    createSelector(
      state => state.entities,
      (_, id) => id,
      (entities, id: number) => denormalize(id, schema, entities),
    ),
  manyByParentId: () =>
    createSelector(
      state => parentSliceSelector(state).ids,
      state => sliceSelector(state),
      state => state.entities,
      (ids, childSlice, entities) =>
        ids.reduce(
          (acc, current) => ({
            ...acc,
            [current]: denormalize(
              childSlice[current]?.ids || [],
              [schema],
              entities,
            ),
          }),
          {},
        ),
    ),
  all: () =>
    createSelector(
      state => sliceSelector(state),
      (_, parentId) => parentId,
      state => state.entities,
      (slice, parentId, entities) =>
        denormalize(slice[parentId]?.ids || [], [schema], entities),
    ),
  ids: () =>
    createSelector(
      state => sliceSelector(state),
      (_, parentId) => parentId,
      (slice, parentId) => slice[parentId]?.ids || [],
    ),
  metaMany: () =>
    createSelector(
      state => sliceSelector(state),
      (_, parentId) => parentId,
      (slice, parentId) => slice[parentId],
    ),
  metaSingleById: () =>
    createSelector(
      state => sliceSelector(state),
      (_state, [parentId, _id]) => parentId,
      (_state, [_parentId, id]) => id,
      (slice, parentId, id) => slice[parentId]?.[id],
    ),
});

/* eslint-disable react-hooks/rules-of-hooks */
export const useGenericNested = <T>(
  schema: any,
  sliceSelector: any => genericNestedStateSliceType,
  parentSliceSelector: any => genericStateSliceType,
): genericNestedSubscriptionHookBundleType<T> => {
  const selectors = genericMemoizedNestedSelectorSet(
    schema,
    sliceSelector,
    parentSliceSelector,
  );

  if (!window.cake[schema.key]) {
    window.cake[schema.key] = 0;
  }

  return {
    singleById: (id: number) => {
      window.cake[schema.key] += 1;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return useSelector(state => useMemo(selectors.singleById, [])(state, id));
    },
    manyByParentId: () => {
      window.cake[schema.key] += 1;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return useSelector(state => useMemo(selectors.manyByParentId, [])(state));
    },
    all: (parentId: number) => {
      window.cake[schema.key] += 1;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return useSelector(state => useMemo(selectors.all, [])(state, parentId));
    },
    ids: (parentId: number) => {
      window.cake[schema.key] += 1;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return useSelector(state => useMemo(selectors.ids, [])(state, parentId));
    },
    metaMany: (parentId: number) =>
      // eslint-disable-next-line react-hooks/exhaustive-deps
      useSelector(state => useMemo(selectors.metaMany, [])(state, parentId)),
    metaSingleById: (parentId: number, id: number) =>
      useSelector(state =>
        // eslint-disable-next-line react-hooks/exhaustive-deps
        useMemo(selectors.metaSingleById, [])(state, [parentId, id]),
      ),
  };
};
/* eslint-enable react-hooks/rules-of-hooks */
